/**
 * Schamane — Kokosnuss (Kokolo, Ketten-Blitz). LMAO-Style (BATTLE-17):
 * kompakt-runde, erdig-braune Faserkugel. Faser-Textur = kurze Strichgruppen in
 * Wuchsrichtung (Meridiane konvergieren zum oberen Pol), drei dunkle Keimloecher
 * oben versetzt (ikonisches Kokos-Merkmal — bewusst KEIN Gesicht, das zeichnet
 * der Renderer mittig). Ein heller Riss-Bogen rechts-unten mit Fruchtfleisch-
 * Akzent (colors.accent). castPulse: Eis-blaues Ketten-Blitz-Knistern (#5bd8f0)
 * aus den Keimloechern. 2★: mehr Fasern + zweiter Riss. 3★: Eis-Energie sickert
 * durch die Risse. Referenz-Qualitaet: kohlrabi.ts / okra.ts. Contract: types.ts.
 */
import type { PaintParams, SpeciesPainter } from './types'
import { darken, glow, lighten, rgba, rimLight, specular, volumeGradient } from '../draw3d'

/** Eis/Blitz-Farbe (docs/13 §6) — Ketten-Blitz des Schamanen, nie fuer den Koerper */
const ICE = '#5bd8f0'

export const paintKokosnuss: SpeciesPainter = (p: PaintParams) => {
  const { ctx, x, y, r, colors, tier, pose } = p
  const dead = p.emotion === 'dead'
  const outlineW = Math.max(2, r * 0.12)
  const rx = r * 0.95
  const ry = r * 0.92

  ctx.save()
  ctx.lineJoin = 'round'
  ctx.translate(x, y)

  const bodyPath = (): void => {
    ctx.beginPath()
    ctx.ellipse(0, 0, rx, ry, 0, 0, Math.PI * 2)
  }

  // --- Haar-Tufts (hinter dem Korpus): Faserbueschel brechen die runde Silhouette ---
  // oben dichter (dort sitzt die Faser-Krone der Nuss); 2★+ wird struppiger
  const tuftAngles = tier >= 2
    ? [-2.55, -2.2, -1.85, -1.5, -1.15, -0.8, -0.45, 2.75, 0.35]
    : [-2.4, -1.95, -1.5, -1.05, -0.6]
  ctx.strokeStyle = darken(colors.body, 24)
  ctx.lineCap = 'round'
  ctx.lineWidth = Math.max(1.2, r * 0.045)
  for (const [i, a] of tuftAngles.entries()) {
    const bx = Math.cos(a) * rx * 0.95
    const by = Math.sin(a) * ry * 0.95
    for (const s of [-1, 0, 1]) {
      // deterministische Variation aus Index — kein Math.random (types.ts)
      const aa = a + s * 0.14 + Math.sin(i * 3.7) * 0.06
      const len = r * (0.16 + 0.05 * Math.sin(i * 2.3 + s))
      ctx.beginPath()
      ctx.moveTo(bx, by)
      ctx.lineTo(bx + Math.cos(aa) * len, by + Math.sin(aa) * len)
      ctx.stroke()
    }
  }

  // --- Korpus: kompakte Kugel mit Volumen (Licht oben-links) ---
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.fillStyle = volumeGradient(ctx, 0, 0, r, colors.body)
  ctx.fill()
  ctx.stroke()

  // --- Innenleben, geclippt auf den Korpus ---
  ctx.save()
  bodyPath()
  ctx.clip()

  // Faser-Textur: kurze Strichgruppen in Wuchsrichtung — Meridiane, die zum
  // oberen Pol (Keimloch-Zone) konvergieren. Face-Zone (Mitte) bleibt ruhig.
  const poleY = -ry * 0.8
  const fiberCluster = (fu: number, fv: number, n: number, len: number): void => {
    const fx = fu * r
    const fy = fv * r
    const ang = Math.atan2(poleY - fy, -fx)
    for (let i = 0; i < n; i++) {
      const off = (i - (n - 1) / 2) * r * 0.06
      const px = fx + Math.cos(ang + Math.PI / 2) * off
      const py = fy + Math.sin(ang + Math.PI / 2) * off
      const l = len * (1 - Math.abs(i - (n - 1) / 2) * 0.2)
      const mx = px + Math.cos(ang + Math.PI / 2) * l * 0.14
      const my = py + Math.sin(ang + Math.PI / 2) * l * 0.14
      ctx.beginPath()
      ctx.moveTo(px - Math.cos(ang) * l * 0.5, py - Math.sin(ang) * l * 0.5)
      ctx.quadraticCurveTo(mx, my, px + Math.cos(ang) * l * 0.5, py + Math.sin(ang) * l * 0.5)
      ctx.stroke()
    }
  }
  ctx.lineCap = 'round'
  // lange Meridian-Boegen an den Flanken: geben Wuchsrichtung + Kugel-Volumen vor
  ctx.strokeStyle = rgba(darken(colors.body, 20), dead ? 0.2 : 0.32)
  ctx.lineWidth = Math.max(1, r * 0.04)
  ctx.beginPath()
  ctx.moveTo(-r * 0.2, -r * 0.8)
  ctx.quadraticCurveTo(-r * 0.85, -r * 0.08, -r * 0.35, r * 0.78)
  ctx.stroke()
  ctx.beginPath()
  ctx.moveTo(r * 0.26, -r * 0.76)
  ctx.quadraticCurveTo(r * 0.9, 0, r * 0.42, r * 0.72)
  ctx.stroke()
  // dunkle Fasern (Schattenseite + unterer Bauch)
  ctx.strokeStyle = rgba(darken(colors.body, 26), dead ? 0.35 : 0.6)
  ctx.lineWidth = Math.max(1, r * 0.035)
  for (const [fu, fv] of [
    [-0.52, 0.52], [-0.15, 0.68], [0.28, 0.66], [0.58, 0.42],
    [0.72, 0.05], [-0.7, 0.22], [0.66, -0.35], [-0.68, -0.38],
  ] as const) {
    fiberCluster(fu, fv, 3, r * 0.2)
  }
  // helle Fasern (Lichtseite oben-links + Glanzkante der Krone)
  ctx.strokeStyle = rgba(lighten(colors.body, 24), dead ? 0.25 : 0.55)
  ctx.lineWidth = Math.max(1, r * 0.03)
  for (const [fu, fv] of [
    [-0.6, -0.45], [-0.42, -0.72], [-0.66, 0.1], [0.5, -0.6], [0.14, 0.55],
  ] as const) {
    fiberCluster(fu, fv, 2, r * 0.17)
  }

  // --- drei Keimloecher oben, versetztes Dreieck (ikonisch, KEIN Gesicht) ---
  const pores = [
    [-0.26, -0.64, 0.08], [0.04, -0.76, 0.09], [0.32, -0.58, 0.075],
  ] as const
  for (const [pu, pv, pr] of pores) {
    const px = pu * r
    const py = pv * r
    const rad = pr * r
    // weiche Mulde um das Loch
    const g = ctx.createRadialGradient(px, py, rad * 0.3, px, py, rad * 1.6)
    g.addColorStop(0, rgba(darken(colors.body, 40), 0.9))
    g.addColorStop(1, rgba(darken(colors.body, 40), 0))
    ctx.fillStyle = g
    ctx.beginPath()
    ctx.arc(px, py, rad * 1.6, 0, Math.PI * 2)
    ctx.fill()
    // Loch-Kern
    ctx.fillStyle = colors.outline
    ctx.beginPath()
    ctx.ellipse(px, py, rad, rad * 1.12, 0, 0, Math.PI * 2)
    ctx.fill()
    // Licht-Kante unten-rechts: Licht oben-links faellt auf die Gegenwand der Mulde
    ctx.strokeStyle = rgba(lighten(colors.body, 28), dead ? 0.3 : 0.65)
    ctx.lineWidth = Math.max(1, r * 0.028)
    ctx.beginPath()
    ctx.ellipse(px, py, rad * 1.12, rad * 1.25, 0, Math.PI * 0.12, Math.PI * 0.88)
    ctx.stroke()
  }

  // --- Riss-Bogen rechts-unten: helles Fruchtfleisch + dunkle Fissur ---
  const tracePath = (pts: ReadonlyArray<readonly [number, number]>, off = 0): void => {
    ctx.beginPath()
    pts.forEach(([cu, cv], i) => {
      if (i === 0) ctx.moveTo(cu * r + off, cv * r + off)
      else ctx.lineTo(cu * r + off, cv * r + off)
    })
  }
  const crackStroke = (pts: ReadonlyArray<readonly [number, number]>): void => {
    // Pass 1: breites Fruchtfleisch-Band (accent)
    ctx.strokeStyle = dead ? rgba(colors.accent, 0.7) : colors.accent
    ctx.lineWidth = Math.max(2, r * 0.075)
    tracePath(pts)
    ctx.stroke()
    // Pass 2: feine dunkle Fissur, leicht nach unten-rechts versetzt (Tiefe)
    ctx.strokeStyle = rgba(colors.outline, 0.75)
    ctx.lineWidth = Math.max(1, r * 0.028)
    tracePath(pts, r * 0.015)
    ctx.stroke()
  }
  const mainCrack = [
    [0.76, -0.12], [0.63, 0.1], [0.71, 0.28], [0.55, 0.46], [0.59, 0.63], [0.4, 0.74],
  ] as const
  ctx.lineCap = 'round'
  crackStroke(mainCrack)
  // kleiner Fruchtfleisch-Splitter an der breitesten Stelle
  ctx.fillStyle = dead ? rgba(colors.accent, 0.7) : colors.accent
  ctx.beginPath()
  ctx.moveTo(r * 0.71, r * 0.24)
  ctx.lineTo(r * 0.6, r * 0.38)
  ctx.lineTo(r * 0.52, r * 0.28)
  ctx.closePath()
  ctx.fill()
  // 2★: zweiter, kuerzerer Riss oben-links (Eskalation additiv, docs/13 §4)
  const sideCrack = [[-0.68, -0.32], [-0.74, -0.06], [-0.62, 0.16]] as const
  if (tier >= 2) crackStroke(sideCrack)

  // 3★: Eis-Energie sickert durch die Risse (pulsiert mit castPulse)
  if (tier >= 3 && !dead) {
    ctx.save()
    ctx.strokeStyle = rgba(ICE, 0.5 + pose.castPulse * 0.4)
    ctx.lineWidth = Math.max(1, r * 0.022)
    ctx.shadowColor = ICE
    ctx.shadowBlur = r * (0.12 + pose.castPulse * 0.15)
    for (const pts of [mainCrack, sideCrack]) {
      tracePath(pts)
      ctx.stroke()
    }
    ctx.restore()
  }
  ctx.restore() // Clip Ende

  // Outline nach dem Innenleben knackig nachziehen (Readability der Silhouette)
  ctx.strokeStyle = colors.outline
  ctx.lineWidth = outlineW
  bodyPath()
  ctx.stroke()

  // --- Cast: Eis-blaues Ketten-Blitz-Knistern aus den Keimloechern ---
  if (!dead && pose.castPulse > 0.02) {
    const a = pose.castPulse
    glow(ctx, 0, -r * 0.55, r * 0.75, ICE, 0.22 * a)
    ctx.save()
    ctx.strokeStyle = rgba(ICE, 0.9 * a)
    ctx.lineWidth = Math.max(1, r * 0.04)
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.shadowColor = ICE
    ctx.shadowBlur = r * (0.16 + a * 0.24)
    // je Keimloch ein Mini-Blitz nach aussen (Zickzack, deterministisch)
    const bolts = [
      [[-0.26, -0.64], [-0.42, -0.8], [-0.36, -0.94], [-0.54, -1.06]],
      [[0.04, -0.76], [0.12, -0.94], [0.02, -1.04], [0.14, -1.22]],
      [[0.32, -0.58], [0.48, -0.7], [0.42, -0.84], [0.6, -0.94]],
    ] as const
    for (const bolt of bolts) {
      tracePath(bolt)
      ctx.stroke()
    }
    // heller Kern in den Loechern (die Nuss laedt sich auf)
    ctx.shadowBlur = r * 0.1
    ctx.fillStyle = rgba(lighten(ICE, 30), 0.85 * a)
    for (const [pu, pv, pr] of [
      [-0.26, -0.64, 0.05], [0.04, -0.76, 0.06], [0.32, -0.58, 0.045],
    ] as const) {
      ctx.beginPath()
      ctx.arc(pu * r, pv * r, pr * r, 0, Math.PI * 2)
      ctx.fill()
    }
    ctx.restore()
  }

  // --- Licht: Rim oben-links + matter Specular (Faser-Schale glaenzt kaum) ---
  if (!dead) {
    rimLight(ctx, 0, 0, rx, ry, 'rgba(255, 248, 232, 0.4)', Math.max(1.5, r * 0.07))
    specular(ctx, -r * 0.3, -r * 0.42, r * 0.13, 0.32)
  }

  ctx.restore()
}
